import React, { useState, useRef, useMemo } from 'react';
import {
  Settings, RefreshCw, AlertTriangle, X, Edit2, ChevronRight, ChevronLeft, ChevronDown, Lock, Unlock, Users, Trash2,
  Download, FileSpreadsheet, FileText, CalendarDays, MessageSquareText
} from 'lucide-react';
import { ClassificationBadge } from '../UI/ClassificationBadge';
import { getClassificationLabel } from '../../utils/classificationConfig';
import DebouncedSearchInput from '../UI/DebouncedSearchInput';
import PillToggle from '../UI/PillToggle';
import SearchableCombobox from '../UI/SearchableCombobox';
import { exportTimesheetPDF, exportTimesheetExcel } from '../../utils/timesheetExport';
import { getLeaveTypeBadgeClass } from '../../utils/leaveTypeBadgeColors';
import { InfoBadge } from '../UI/InfoBadge';
import { TradeBadge } from '../UI/TradeBadge';
import { isTradeRedundantWithDepartment } from '../../config/tradeConfig';
import { getDepartmentBadgeColor, getDepartmentShortName } from '../../config/departmentConfig';
import { SessionPeriodChip } from '../UI/SessionPeriodChip';
import { formatDate, formatDateTimeCompact } from '../../utils/dateUtils';
import { isDayLockableNow, getLockBlockReason, isDayRenormalizableNow } from '../../hooks/lockableDay';

const deriveFromSites = (sites, key, agg) => {
  if (!sites || sites.length === 0) return null;
  const vals = sites.map(s => s[key]).filter(Boolean);
  if (vals.length === 0) return null;
  if (agg === 'min') return vals.reduce((a, b) => a < b ? a : b);
  if (agg === 'max') return vals.reduce((a, b) => a > b ? a : b);
  return vals[0];
};

const sumSiteMetric = (sites, key) => (
  (sites || []).reduce((sum, site) => sum + (parseFloat(site?.[key]) || 0), 0)
);

const getDisplayWeekdayOt = (rec) => (
  rec?.sites?.length ? sumSiteMetric(rec.sites, 'weekday_ot_hours') : (rec?.weekday_ot_hours || 0)
);

const getDisplayRestDayOt = (rec) => (
  rec?.sites?.length ? sumSiteMetric(rec.sites, 'rest_day_ot_hours') : (rec?.rest_day_ot_hours || 0)
);

const getDisplayRegularTotal = (rec) => {
  const siteRegular = sumSiteMetric(rec?.sites, 'regular_hours');
  if (siteRegular > 0) return siteRegular;
  return Math.max(0, (rec?.total_hours || 0) - getDisplayWeekdayOt(rec) - getDisplayRestDayOt(rec));
};

// Human-readable "who locked this, and when" line for lock-control tooltips.
// With 4 authorized lockers, this is the at-a-glance accountability/coordination
// signal HR relies on before unlocking someone else's row. Returns null when no
// attribution is available (e.g. locked by a since-offboarded admin → SET_NULL).
// Timestamp uses DS formatDateTimeCompact ("8 Jul 2026, 11:26 AM").
const formatLockAttribution = (rec) => {
  if (!rec || rec.lock_status !== 'locked') return null;
  const name = rec.lock_applied_by_name || rec.lock_applied_by;
  if (!name) return null;
  const when = rec.lock_applied_at ? formatDateTimeCompact(rec.lock_applied_at) : null;
  return when ? `Locked by ${name} · ${when}` : `Locked by ${name}`;
};

// Employee-level attribution derived from the loaded daily records. With 4
// lockers a period can be frozen by more than one person (some rows by Mary,
// some by John); surface every distinct name so HR knows who to consult before
// unlocking the whole employee. Returns null when nothing is attributable.
const formatEmployeeLockAttribution = (records) => {
  const locked = (records || []).filter(
    r => r.lock_status === 'locked' && !r.isPreEmployment && !r.isPostEmployment
  );
  if (locked.length === 0) return null;
  const names = [...new Set(
    locked.map(r => r.lock_applied_by_name || r.lock_applied_by).filter(Boolean)
  )];
  if (names.length === 0) return null;
  if (names.length === 1) return `Locked by ${names[0]}`;
  return `Locked by ${names.join(', ')}`;
};

const siteMatchesSearch = (site, term) => {
  const haystack = [
    site?.site_name,
    site?.site_address,
    site?.project_code,
    site?.project_name,
    site?.project?.project_code,
    site?.project?.project_name,
  ];
  return haystack.some((value) => String(value || '').toLowerCase().includes(term));
};

const isEmployeeFullyLocked = (employee, dailyRecords) => {
  if (!dailyRecords || dailyRecords.length === 0) return false;
  const effectiveRecords = dailyRecords.filter(rec => !rec.isPreEmployment && !rec.isPostEmployment);
  if (effectiveRecords.length === 0) return false;
  return effectiveRecords.every(rec => rec.lock_status === 'locked');
};

const isRecordComplete = (rec) => {
  if (rec.isPreEmployment || rec.isPostEmployment) return true;
  if (rec.day_type !== 'working') return true;
  return rec.adjusted_clock_in && rec.adjusted_clock_out && rec.total_hours > 0 && rec.sites && rec.sites.length > 0;
};

const canLockEmployee = (employee, dailyRecords) => {
  if (!dailyRecords || dailyRecords.length === 0) return false;
  const workingRecs = dailyRecords.filter(r => r.day_type === 'working' && !r.isPreEmployment && !r.isPostEmployment);
  return workingRecs.length > 0 && workingRecs.every(isRecordComplete);
};

const SCHEDULE_OVERRIDE_INFO = {
  day_off: {
    label: 'Day Off',
    color: 'gray',
    tooltip: 'Schedule override — Day Off. Not expected to work this day.',
  },
  full_day: {
    label: 'Full Day',
    color: 'green',
    tooltip: 'Schedule override — Full Day Work. Expected to work a full shift this day (not the usual half-day, when applicable).',
  },
  custom: {
    label: 'Custom',
    color: 'blue',
    tooltip: 'Schedule override — Custom hours.',
  },
};

function getScheduleOverrideInfo(rec) {
  const base = SCHEDULE_OVERRIDE_INFO[rec?.schedule_override_type];
  if (!base) return null;
  const reason = (rec.schedule_override_reason || '').trim();
  return {
    type: rec.schedule_override_type,
    label: base.label,
    color: base.color,
    title: reason ? `${base.tooltip} ${reason}` : base.tooltip,
  };
}

function recordHasClockData(rec) {
  return !!(
    rec?.raw_clock_in
    || rec?.adjusted_clock_in
    || deriveFromSites(rec?.sites, 'raw_clock_in', 'min')
    || deriveFromSites(rec?.sites, 'clock_in', 'min')
    || (rec?.total_hours && rec.total_hours > 0)
  );
}

function hasRealClockIn(rec) {
  return !!(
    rec?.raw_clock_in
    || rec?.actual_clock_in
    || deriveFromSites(rec?.sites, 'raw_clock_in', 'min')
  );
}

// Early-morning NW (00:00–04:00) shares its request date with full-day
// recovery. generate_calendar_days keeps day_type='nightwork' on that
// date, while the worker is actually resting. Synthetic 8h + adjusted
// 08:00–17:00 must not fall through to Present (W0066 / 12-Aug-2026).
function isFullDayRecoveryRest(rec) {
  return rec?.recovery_type === 'full_day' && !hasRealClockIn(rec);
}

function isDayOffStatusChip(rec) {
  return rec?.schedule_override_type === 'day_off' && !recordHasClockData(rec);
}

function ScheduleOverrideDateBadge({ rec, statusIsDayOff }) {
  const info = getScheduleOverrideInfo(rec);
  if (!info) return null;
  if (info.type === 'day_off' && statusIsDayOff) return null;
  return (
    <InfoBadge
      color={info.color}
      label={info.label}
      size="xs"
      title={info.title}
    />
  );
}

function getLastWorkingSessionInfo(rec, employee) {
  const endIso = String(
    rec?.employment_end_date
    || employee?.employment_end_date
    || employee?.final_working_day
    || ''
  ).slice(0, 10);
  const isLastWorkingDay = Boolean(
    rec?.employment_end_session
    || (endIso && rec?.date === endIso)
  );
  if (!isLastWorkingDay) return null;
  const session = (
    rec?.employment_end_session
    || employee?.employment_end_session
    || employee?.final_working_session
    || ''
  ).toUpperCase();
  if (session !== 'AM') return null;
  return {
    label: 'Last AM',
    color: 'sky',
    title: 'Last working day — employment ends after the morning session. Adjusted out is lunch start, not the full-day schedule end.',
  };
}

function LastWorkingSessionBadge({ rec, employee }) {
  const info = getLastWorkingSessionInfo(rec, employee);
  if (!info) return null;
  return (
    <InfoBadge
      color={info.color}
      label={info.label}
      size="xs"
      title={info.title}
    />
  );
}

const getMobileStatusBadge = (rec) => {
  if (rec.isPreEmployment) return { label: 'Not yet joined', cls: 'bg-gray-200 text-gray-500' };
  if (rec.isPostEmployment) return { label: 'Inactive', cls: 'bg-gray-200 text-gray-500' };
  if (rec.is_attendance_exempt) return { label: 'Exempt', cls: 'bg-purple-100 text-purple-700' };
  if (rec.is_public_holiday) return { label: 'PH', cls: 'bg-purple-100 text-purple-700' };
  if (rec.recovery_type === 'am_only') return { label: 'Recovery (AM)', cls: 'bg-yellow-50 text-yellow-800' };
  if (rec.day_type === 'recovery' || isFullDayRecoveryRest(rec)) return { label: 'Recovery', cls: 'bg-yellow-50 text-yellow-800' };
  const leaves = rec.leave_applications?.length
    ? rec.leave_applications
    : rec.leave_application ? [rec.leave_application] : [];
  if (leaves.length > 0) {
    const la = leaves[0];
    const shortName = la.leave_type_abbr || la.leave_type || 'Leave';
    const label = la.is_half_day && la.leave_period ? `${shortName} (${la.leave_period})` : shortName;
    return { label, cls: 'bg-amber-100 text-amber-700' };
  }
  if (rec.day_type === 'absent') return { label: 'Absent', cls: 'bg-red-100 text-red-700' };
  const hasClock = rec.raw_clock_in || rec.adjusted_clock_in || (rec.total_hours && rec.total_hours > 0);
  if ((rec.is_rest_day || rec.is_weekend) && !hasClock) {
    if (rec.schedule_override_type === 'day_off') {
      return {
        label: 'Day Off',
        cls: 'bg-gray-100 text-gray-500',
        title: getScheduleOverrideInfo(rec)?.title,
      };
    }
    return { label: 'Rest Day', cls: 'bg-gray-100 text-gray-500' };
  }
  if (rec.day_type === 'upcoming') return { label: 'Upcoming', cls: 'bg-gray-50 text-gray-400' };
  if (hasClock) return { label: 'Present', cls: 'bg-green-100 text-green-700' };
  return { label: '-', cls: 'bg-gray-50 text-gray-400' };
};

const getSiteSummaryLabel = (rec) => {
  const hasSites = rec.sites && rec.sites.length > 0;
  if (!hasSites) {
    return rec.project_code || rec.project_name || (rec.is_paid_leave ? 'Unallocated' : '-');
  }
  if (rec.sites.length > 1) return `${rec.sites.length} sites`;
  const s0 = rec.sites[0];
  return s0?.site_name || s0?.project_code || rec.project_code || (rec.is_paid_leave ? 'Unallocated' : '-');
};

// ---------------------------------------------------------------------------
// Daily-records table column widths (px)
//
// We use ``table-fixed`` + an explicit ``<colgroup>`` so column widths stay
// stable when accordion rows expand/collapse. Auto-layout would otherwise
// recompute every column's max-content from the now-visible child rows
// (which carry full project / location strings) and visibly shift the layout
// every time the user opens a parent row.
//
// Widths are tuned from real production data, not eyeballed. The audit lives
// at ``scripts/_audit_sites_column_width.py`` and reports, against
// ``AttendanceRecordSession.location`` (n=15,982 rendered rows):
//
//   median = 15 chars     p95 = 30 chars     p99 = 41 chars     max = 45 chars
//
// At ``text-xs font-medium`` the average glyph width in our font stack is
// ~6.9 px, plus 48 px cell padding chrome (px-6 = 24 px each side). p95 maps
// to ~255 px -- we size Sites to 256 px so anything up to and including the
// 95th-percentile real value fits without truncation, and outliers ellide
// gracefully via ``truncate`` with the full name preserved in the cell
// tooltip. This mirrors the geofence p95 policy in
// ``.cursor/rules/geofence-radius-policy.mdc``: size to p95, treat the long
// tail as outliers worth handling with a fallback (here: ellipsis + tooltip)
// rather than over-allocating width to every row.
//
// The dense numeric columns (HH:MM clocks, X.XXh hours) are sized to their
// fixed-format content plus padding. They were previously over-allocated
// under auto-layout because ``Workday OT`` etc. demand more width on the
// header than on the values; we keep just enough header width and let the
// value cells inherit it.
//
// If you add a column or change content, re-run the audit and re-tune.
// ---------------------------------------------------------------------------
const COLUMN_WIDTHS_PX = {
  checkbox:    48,  // single 16x16 checkbox + light padding
  dateDay:    176,  // date + weekday + optional Full Day / Custom InfoBadge
  status:     128,  // worst case: stacked chips like "Recovery (AM)" + "Present (PM)"
  sites:      256,  // p95 of session.location (30 chars) + 48 px chrome; ellide above
  actualIn:    80,  // HH:MM
  actualOut:   88,  // HH:MM + optional "+1d" badge
  adjustedIn:  84,  // HH:MM
  adjustedOut: 88,  // HH:MM + optional "+1d" badge
  total:       76,  // X.XXh
  lunch:       72,  // X.Xh
  dinner:      72,  // X.Xh
  workdayOt:  100,  // header "Workday OT" is the binding constraint, not the value
  restDayOt:  100,  // header "Rest Day OT" is the binding constraint
  actions:     72,  // single 32x32 icon button (Lock / Unlock)
};

export function AdjustAndLockTable(props) {
  const {
    employeeList, selectedEmployee, employeeDailyRecords, periodLabel, onRefresh, isRefreshing, canRefresh,
    conflictWarning, onDismissConflict, hasUnsavedEdits, onSelectEmployee, onToggleEmployeeLock,
    selectedRows, setSelectedRows, lastSelectedIndex, setLastSelectedIndex, expandedRows, onToggleRow,
    editingCell, setEditingCell, cellValue, setCellValue, savingCell, editingSiteCell,
    leftPanelCollapsed, setLeftPanelCollapsed, employeeSearchTerm, setEmployeeSearchTerm,
    filterClassification, setFilterClassification, onSelectAll, onRowSelect, onCellClick, onCellSave, onSessionCellSave,
    onSiteClick, onSiteSave, onRowLockToggle, onRowRenormalize, rowRenormalizing, onBulkLock, onBulkUnlock, onClearSelection, onDeleteSession,
    getFilteredAndSortedSites, siteFrequencyMap = {}, getSiteFrequency: getSiteFrequencyProp, isRowEditable, isEmployeeFullyLocked: isEmpLocked, canLockEmployee: canLock,
    rowLocking, todayIso, deriveFromSites: derive
  } = props;
  const deriveFn = derive || deriveFromSites;
  const canEditAdjustedClock = (rec) => (
    isRowEditable(rec)
    && !!(rec.attendance_record_id || rec.id)
    && !rec.needs_normalization
  );
  const adjustedClockEditTitle = (rec) => {
    if (rec.lock_status === 'locked') return 'Unlock this day before editing adjusted times';
    if (rec.needs_normalization || !(rec.attendance_record_id || rec.id)) {
      return 'Re-normalize this day before editing adjusted times';
    }
    return canEditAdjustedClock(rec) ? 'Edit payable time' : undefined;
  };
  const isEmpLockedFn = isEmpLocked || isEmployeeFullyLocked;
  const canLockFn = canLock || canLockEmployee;
  const recs = employeeDailyRecords || [];
  const sel = selectedEmployee;
  const visibleEmployees = useMemo(() => (
    employeeList.filter((emp) => {
      if (employeeSearchTerm) {
        const term = employeeSearchTerm.toLowerCase();
        const name = (emp.staff_name || '').toLowerCase();
        const staffId = (emp.staff_id || '').toLowerCase();
        if (!name.includes(term) && !staffId.includes(term)) return false;
      }
      if (filterClassification !== 'all') {
        const empCls = emp.classification && emp.classification !== 'N/A' ? emp.classification : 'unclassified';
        if (empCls !== filterClassification) return false;
      }
      return true;
    })
  ), [employeeList, employeeSearchTerm, filterClassification]);
  const selectedLockReadyReason = recs.find(rec => rec.lock_ready_reason)?.lock_ready_reason || null;
  const [exporting, setExporting] = useState(null);
  const exportingRef = useRef(false);
  const [openNoteDate, setOpenNoteDate] = useState(null);

  const handleDownloadPDF = async () => {
    if (!sel || !recs.length || exportingRef.current) return;
    exportingRef.current = true;
    setExporting('pdf');
    try { await exportTimesheetPDF(sel, recs, periodLabel); }
    finally { setExporting(null); exportingRef.current = false; }
  };

  const handleDownloadExcel = async () => {
    if (!sel || !recs.length || exportingRef.current) return;
    exportingRef.current = true;
    setExporting('xlsx');
    try { await exportTimesheetExcel(sel, recs, periodLabel); }
    finally { setExporting(null); exportingRef.current = false; }
  };

  const renderSiteCombobox = ({ rec, currentLabel, sessionIdOrIndex = null, placeholder = 'Search sites...' }) => {
    const sites = getFilteredAndSortedSites();
    return (
      // Sites column is intentionally narrow (p95 cell width); the menu must
      // open wider so HR can read full project name + code before assigning.
      <div className="relative site-dropdown-container w-full">
        <SearchableCombobox
          items={sites}
          value=""
          displayValue={currentLabel}
          autoOpen
          onChange={(selectedWorkSiteId) => {
            const selectedSite = sites.find((s) => String(s.id) === String(selectedWorkSiteId));
            if (selectedSite) onSiteSave(rec, selectedSite, sessionIdOrIndex);
          }}
          placeholder={placeholder}
          searchPlaceholder={placeholder}
          emptyLabel="No sites found"
          displayFn={(site) => site.site_name || site.project_code || '-'}
          searchFn={siteMatchesSearch}
          valueFn={(site) => site.id}
          // Truthy menuMinWidth enables the 360px portaled menu (readable
          // name+code without spanning the attendance grid).
          menuMinWidth="site-picker"
          renderItem={({ item, itemValue, onMouseDownSelect, isHighlighted }) => {
            const freq = getSiteFrequencyProp
              ? getSiteFrequencyProp(item)
              : (siteFrequencyMap[item.id] || siteFrequencyMap[item.site_name] || 0);
            const isFrequent = freq >= 3;
            const siteName = item.site_name || '-';
            const projectCode = item.project_code || '-';
            return (
              <button
                key={itemValue}
                type="button"
                onMouseDown={onMouseDownSelect}
                title={`${siteName}${projectCode !== '-' ? ` (${projectCode})` : ''}`}
                className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 ${
                  isHighlighted ? 'bg-red-50 text-gray-900' : 'text-gray-700 hover:bg-red-50'
                }`}
              >
                <div className="flex items-start justify-between gap-2 text-left">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 break-words">
                      {siteName}
                    </div>
                    <div className="text-gray-500 text-xs mt-0.5 whitespace-nowrap">
                      {projectCode}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 pt-0.5">
                    {isFrequent && (
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700">
                        *
                      </span>
                    )}
                    {freq > 0 && (
                      <span className="text-gray-400 text-[10px]">
                        {freq}x
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          }}
          theme="admin"
          className="w-full text-xs"
        />
      </div>
    );
  };

  return (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Settings className="w-5 h-5 text-red-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">Adjust & Lock</h3>
            </div>
                    <div className="flex items-center gap-2">
                      {sel && recs.length > 0 && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleDownloadExcel}
                            disabled={!!exporting || isRefreshing}
                            className="btn btn-sm btn-admin-outline"
                            title={`Download timesheet for ${sel.staff_name || sel.staff_id} (.xlsx)`}
                          >
                            {exporting === 'xlsx' ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <FileSpreadsheet className="w-4 h-4" />
                            )}
                            Excel
                          </button>
                          <button
                            onClick={handleDownloadPDF}
                            disabled={!!exporting || isRefreshing}
                            className="btn btn-sm btn-admin-outline"
                            title={`Download timesheet for ${sel.staff_name || sel.staff_id} (.pdf)`}
                          >
                            {exporting === 'pdf' ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <FileText className="w-4 h-4" />
                            )}
                            PDF
                          </button>
                        </div>
                      )}
                    <button
                      onClick={onRefresh}
                      disabled={!canRefresh || isRefreshing}
                      className="icon-button-admin"
                      title="Re-fetch and re-normalize attendance data"
                      aria-label={isRefreshing ? 'Refreshing data…' : 'Refresh data'}
                    >
                      <RefreshCw className={`w-4 h-4${isRefreshing ? ' animate-spin' : ''}`} />
                    </button>
                    </div>
        </div>
                  <p className="text-sm text-gray-600">
                    Review each employee's attendance, make adjustments if needed, then lock for payroll processing.
                  </p>
                  {/* Conflict Warning Banner */}
                {conflictWarning && (
                  <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-r-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <AlertTriangle className="w-5 h-5 text-orange-600 mr-3 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-orange-900">
                            {conflictWarning.message}
                          </p>
                          {conflictWarning.lockedBy && (
                            <p className="text-xs text-orange-700 mt-1">
                              Locked by: {conflictWarning.lockedBy} at {formatDateTimeCompact(conflictWarning.lockedAt)}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => onDismissConflict(null)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-orange-600 hover:text-orange-800 hover:bg-orange-100 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-1"
                        title="Dismiss"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Unsaved Edits Indicator */}
                {hasUnsavedEdits && (
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-lg">
                    <div className="flex items-center">
                      <Edit2 className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                      <p className="text-sm text-blue-800">
                        You have unsaved edits. They will be preserved if you refresh the page.
                      </p>
                    </div>
                  </div>
                )}

                {/* Master-Detail Layout */}
                {employeeList.length > 0 ? (
                  <div className="flex gap-6 min-h-[400px]" style={{ height: 'calc(100dvh - 240px)' }}>
                    {/* LEFT PANEL: Employee List — fixed width so search + classification
                        PillToggle (All / Staff / Worker + CountBadges) always fit on desktop.
                        w-1/6 was too narrow and clipped the active segment. */}
                    <div className={`${leftPanelCollapsed ? 'w-14 flex-shrink-0' : 'w-72 xl:w-80 flex-shrink-0 min-w-0'} bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden transition-all duration-200`}>
                      {leftPanelCollapsed && (
                        <div className="flex flex-col items-center pt-2 gap-1 h-full">
                          <button
                            onClick={() => setLeftPanelCollapsed(false)}
                            className="icon-button-admin mb-1"
                            title="Expand panel"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                          <div className="flex-1 overflow-y-auto scrollbar-hide w-full flex flex-col items-center gap-1.5 pb-2 px-1">
                            {employeeList
                              .filter(emp => {
                                if (filterClassification !== 'all') {
                                  const empCls = emp.classification && emp.classification !== 'N/A' ? emp.classification : 'unclassified';
                                  if (empCls !== filterClassification) return false;
                                }
                                return true;
                              })
                              .map(emp => {
                                const isSelected = selectedEmployee?.staff_id === emp.staff_id;
                                const initials = (emp.staff_name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                                return (
                                  <button
                                    key={emp.staff_id}
                                    onClick={() => onSelectEmployee(emp)}
                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 transition-all duration-200 border-2 focus-visible:outline-none focus-visible:border-red-500 ${
                                      isSelected
                                        ? 'bg-red-600 text-white border-red-300'
                                        : 'bg-red-100 text-red-600 hover:bg-red-200 border-transparent'
                                    }`}
                                    title={`${emp.staff_name} (${emp.staff_id})`}
                                  >
                                    {initials}
                                  </button>
                                );
                              })}
                          </div>
                        </div>
                      )}
                      
                      {!leftPanelCollapsed && (
                        <>
                      {/* Search & Filters */}
                      <div className="p-4 border-b border-gray-200 min-w-0">
                        <div className="flex items-center gap-2 mb-3 min-w-0">
                          <DebouncedSearchInput
                            value={employeeSearchTerm}
                            onSearch={setEmployeeSearchTerm}
                            placeholder="Search employees..."
                            className="min-w-0"
                          />
                          <button
                            onClick={() => setLeftPanelCollapsed(true)}
                            className="icon-button-admin flex-shrink-0"
                            title="Collapse panel"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Classification filter — only shown when 2+ distinct classifications exist.
                            Scope control for HR: must remain fully visible (counts + active pill). */}
                        {(() => {
                          const counts = {};
                          employeeList.forEach(emp => {
                            const cls = emp.classification && emp.classification !== 'N/A' ? emp.classification : 'unclassified';
                            counts[cls] = (counts[cls] || 0) + 1;
                          });
                          const distinctClassifications = Object.keys(counts);
                          if (distinctClassifications.length < 2) return null;
                          const opts = [{ id: 'all', label: 'All', count: employeeList.length }];
                          const order = ['staff', 'worker', 'intern', 'advisor', 'managing_director', 'executive_director', 'non_executive_director', 'system', 'unclassified'];
                          order.forEach(cls => {
                            if (counts[cls]) {
                              opts.push({
                                id: cls,
                                label: cls === 'unclassified' ? 'Unclassified' : getClassificationLabel(cls),
                                count: counts[cls],
                              });
                            }
                          });
                          // ≤4 segments: equal-width track (fits All/Staff/Worker + badges in w-72/w-80).
                          // 5+: natural width + track scroll (DS: pill-toggle-container overflow-x-auto).
                          return (
                            <PillToggle
                              value={filterClassification}
                              onChange={setFilterClassification}
                              options={opts}
                              variant="text"
                              theme="admin"
                              ariaLabel="Filter employees by classification"
                              className="w-full max-w-full"
                              fullWidth={opts.length <= 4}
                            />
                          );
                        })()}
                      </div>

                      {/* Employee List */}
                      <div className="flex-1 overflow-y-auto">
                        {visibleEmployees.length === 0 ? (
                          <div className="text-center py-12 px-3">
                            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No employees found</h3>
                            <p className="text-sm text-gray-500">
                              {employeeSearchTerm
                                ? 'Try a different name or staff ID.'
                                : 'Choose another classification, or show everyone.'}
                            </p>
                          </div>
                        ) : visibleEmployees.map(emp => {
                            const isSelected = selectedEmployee?.staff_id === emp.staff_id;
                            const isLocked = isSelected 
                              ? isEmployeeFullyLocked(emp, employeeDailyRecords)
                              : (emp.lock_status === 'locked');
                            const canLock = isSelected ? canLockFn(employeeDailyRecords) : true;
                            const isDisabled = !isLocked && !canLock;
                            return (
                              // Row is a div (not <button>) so the Lock control can be a real
                              // <button> without nested-interactive HTML. Browsers close an
                              // outer <button> early when they hit an inner one, which orphans
                              // the lock outside the flex row — long names then paint under it.
                              <div
                                key={emp.staff_id}
                                role="button"
                                tabIndex={0}
                                onClick={() => onSelectEmployee(emp)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onSelectEmployee(emp);
                                  }
                                }}
                                className={`w-full text-left p-3 cursor-pointer transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-500 ${
                                  isSelected 
                                    ? 'bg-red-50 border-l-4 border-red-500' 
                                    : 'hover:bg-gray-50 border-l-4 border-transparent'
                                }`}
                              >
                                {/*
                                  Lock pins to the status-chip row (items-end), not the name.
                                  HR scans "X/Y locked" → locks. Name truncates in its own
                                  column; full name on hover via title.
                                */}
                                <div className="flex items-end justify-between gap-2">
                                  <div className="min-w-0 flex-1">
                                    <div
                                      className="font-medium text-gray-900 truncate text-sm"
                                      title={emp.staff_name}
                                    >
                                      {emp.staff_name}
                                    </div>
                                    <div className="text-xs text-gray-500">{emp.staff_id}</div>
                                    <div className="flex items-center gap-1.5 flex-wrap min-w-0 mt-0.5">
                                      {emp.department_name && emp.department_name !== 'N/A' && (
                                        <InfoBadge
                                          color={getDepartmentBadgeColor(emp.department_name)}
                                          label={getDepartmentShortName(emp.department_name)}
                                          title={emp.department_name}
                                          size="xs"
                                        />
                                      )}
                                      {emp.trade_code &&
                                        !isTradeRedundantWithDepartment(emp.department_name, emp.trade_code) && (
                                        <TradeBadge code={emp.trade_code} size="xs" />
                                      )}
                                    </div>
                                    {/* Lock Progress Chip + Probation/Exception badges */}
                                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                                      {/* Partial Lock Status Chip */}
                                      {emp.total_days > 0 && (() => {
                                        // Build tooltip explaining the employment-window
                                        // truncation when the chip's denominator is
                                        // smaller than the calendar month (mid-period
                                        // joiner / leaver). Without this, a curious
                                        // reviewer wondering "where are the other
                                        // days?" has no progressive disclosure path.
                                        const formatShort = (iso) => {
                                          if (!iso) return '';
                                          const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
                                          if (!m) return iso;
                                          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                                          return `${parseInt(m[3], 10)} ${months[parseInt(m[2], 10) - 1]}`;
                                        };
                                        const windowParts = [];
                                        if (emp.joined_mid_period && emp.employment_start_date) {
                                          windowParts.push(`joined ${formatShort(emp.employment_start_date)}`);
                                        }
                                        if (emp.left_mid_period && emp.employment_end_date) {
                                          windowParts.push(`last day ${formatShort(emp.employment_end_date)}`);
                                        }
                                        const windowSuffix = windowParts.length > 0 ? ` (${windowParts.join(', ')})` : '';
                                        const title = `${emp.locked_count} of ${emp.total_days} employed days locked${windowSuffix}`;
                                        return (
                                          <span
                                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                              emp.locked_count === emp.total_days
                                                ? 'bg-green-100 text-green-700 border border-green-200'
                                                : emp.locked_count === 0
                                                  ? 'bg-red-100 text-red-700 border border-red-200'
                                                  : 'bg-amber-100 text-amber-700 border border-amber-200'
                                            }`}
                                            title={title}
                                            aria-label={title}
                                          >
                                            {emp.locked_count}/{emp.total_days} locked
                                          </span>
                                        );
                                      })()}
                                      {emp.is_probation && (
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-700">
                                          Probation
                                        </span>
                                      )}
                                      {emp.has_exceptions && (
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700">
                                          Exception
                                        </span>
                                      )}
                                      {/* Exempt — Deemed Present chip
                                          ───────────────────────────────────────────────
                                          Surfaces the "Deemed Present (Exempt)" PLC
                                          contract for staff who don't clock in/out
                                          (managing_director / executive_director /
                                          advisor / etc., or attendance_tracking_mode
                                          = 'exempt').  See
                                          .cursor/rules/attendance-exempt-deemed-present.mdc
                                          for the full data model.  Tooltip exposes
                                          the resolved bucket project (per-employee
                                          override → dept default → P0000-018) so
                                          reviewers know where the basic salary lands
                                          in the PLC report. */}
                                      {emp.is_attendance_exempt && (
                                        <span
                                          className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700 border border-purple-200"
                                          title={
                                            emp.exempt_default_project_code
                                              ? `Exempt — Deemed Present. Salary attributed to ${emp.exempt_default_project_code}${emp.exempt_default_project_name ? ' (' + emp.exempt_default_project_name + ')' : ''}.`
                                              : 'Exempt — Deemed Present (no clocking required).'
                                          }
                                        >
                                          Exempt — Deemed Present
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex-shrink-0 self-end pb-0.5">
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); onToggleEmployeeLock(emp); }}
                                      disabled={isDisabled}
                                      aria-label={
                                        isDisabled
                                          ? `Cannot lock ${emp.staff_name}: incomplete data`
                                          : isLocked
                                            ? `Unlock ${emp.staff_name}`
                                            : `Lock ${emp.staff_name}`
                                      }
                                      className={`inline-flex items-center justify-center w-7 h-7 rounded-full border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-red-500 ${
                                        isLocked ? 'bg-green-100 border-green-300' : 'bg-yellow-100 border-yellow-300'
                                      } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      title={
                                        isDisabled
                                          ? 'Cannot lock: Incomplete data'
                                          : isLocked
                                            ? ((isSelected && formatEmployeeLockAttribution(employeeDailyRecords))
                                                ? `${formatEmployeeLockAttribution(employeeDailyRecords)}\nClick to unlock`
                                                : 'Unlock')
                                            : selectedLockReadyReason || 'Lock'
                                      }
                                    >
                                      {isLocked ? (
                                        <Lock className="w-3.5 h-3.5 text-green-700" />
                                      ) : (
                                        <Unlock className="w-3.5 h-3.5 text-yellow-700" />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                      </>
                      )}
                    </div>

                    {/* RIGHT PANEL: Daily Detail — fills remaining space beside fixed-width list */}
                    <div className="flex-1 min-w-0 bg-white rounded-xl border border-gray-200 flex flex-col transition-all duration-200">
                      {selectedEmployee ? (
                        <>
                          {/* Employee Header */}
                          <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                              <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="text-lg font-semibold text-gray-900">
                                  {selectedEmployee.staff_name} • {selectedEmployee.staff_id}
                                </h4>
                                {selectedEmployee.classification && selectedEmployee.classification !== 'N/A' && (
                                  <ClassificationBadge
                                    classification={selectedEmployee.classification}
                                    variant="solid"
                                  />
                                )}
                              </div>
                                <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5 flex-wrap">
                                  {selectedEmployee.department_name && selectedEmployee.department_name !== 'N/A' && (
                                    <InfoBadge
                                      color={getDepartmentBadgeColor(selectedEmployee.department_name)}
                                      label={selectedEmployee.department_name}
                                      title={selectedEmployee.department_name}
                                      size="xs"
                                    />
                                  )}
                                  {selectedEmployee.trade_code &&
                                    !isTradeRedundantWithDepartment(
                                      selectedEmployee.department_name,
                                      selectedEmployee.trade_code,
                                    ) && (
                                    <TradeBadge code={selectedEmployee.trade_code} size="xs" />
                                  )}
                                  {selectedEmployee.work_schedule_type && (
                                    <>
                                      <span className="text-gray-300">·</span>
                                      <span className="inline-flex items-center gap-1">
                                        <CalendarDays className="w-3 h-3 text-gray-400" />
                                        {selectedEmployee.work_schedule_type}
                                      </span>
                                    </>
                                  )}
                                  {selectedEmployee.employment_type === 'part_time' && (
                                    <>
                                      <span className="text-gray-300">·</span>
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-orange-50 text-orange-600 font-medium">Part-time</span>
                                    </>
                                  )}
                                  {/* Exempt bucket attribution
                                      ─────────────────────────────────────────
                                      For attendance-exempt staff, surface the
                                      project that absorbs their basic salary
                                      in PLC.  Mirrors the chip in the left
                                      panel so reviewers don't need to expand
                                      the calendar to know "where is this
                                      director's cost going?". */}
                                  {selectedEmployee.is_attendance_exempt && selectedEmployee.exempt_default_project_code && (
                                    <>
                                      <span className="text-gray-300">·</span>
                                      <span
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 font-medium"
                                        title="Resolved bucket: per-employee override → dept default → P0000-018 (WEPL Main Office) fallback."
                                      >
                                        Exempt → {selectedEmployee.exempt_default_project_code}
                                        {selectedEmployee.exempt_default_project_name ? ` · ${selectedEmployee.exempt_default_project_name}` : ''}
                                      </span>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-sm">
                                  <span className="text-gray-600">Period:</span>
                                  <span className="text-gray-900 font-medium">{periodLabel}</span>
                                  <span className="text-gray-400">•</span>
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    isEmployeeFullyLocked(selectedEmployee, employeeDailyRecords)
                                      ? 'bg-green-100 text-green-800 border border-green-200' 
                                      : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                  }`}>
                                    {isEmployeeFullyLocked(selectedEmployee, employeeDailyRecords) ? 'Locked' : 'Unlocked'}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {/* Legend (aligned with AttendanceManagement) */}
                                <div className="hidden md:flex items-center gap-3 text-xs text-gray-700 mr-3">
                                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-200 border border-green-300"></span>Locked</span>
                                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-200 border border-purple-300"></span>Holiday</span>
                                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-200"></span>Leave</span>
                                  <span className="inline-flex items-center gap-1" title="Half-day leave or AM-only recovery — left amber/yellow bar on the row">
                                    <span className="w-3 h-3 rounded bg-white border border-gray-200 border-l-2 border-l-amber-400"></span>Half-day
                                  </span>
                                  <span className="inline-flex items-center gap-1" title="Template rest day, or a scheduled Day Off override">
                                    <span className="w-3 h-3 rounded bg-gray-200 border border-gray-300"></span>Rest day
                                  </span>
                                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200 border border-red-300"></span>Absent</span>
                                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-100 border border-gray-200"></span>Upcoming</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Daily Records Table with Accordion Headers.
                              Column widths come from ``COLUMN_WIDTHS_PX`` -- tuned from
                              the real-data audit; see the constant's docblock for the
                              p95 rationale and re-tuning instructions. */}
                          <div className="flex-1 overflow-auto relative focus:outline-none">
                            <div className="md:hidden space-y-3 p-4">
                              {employeeDailyRecords.map((rec, rowIndex) => {
                                const isLocked = rec.lock_status === 'locked';
                                const status = getMobileStatusBadge(rec);
                                const adjIn = deriveFromSites(rec.sites, 'clock_in', 'min') || rec.adjusted_clock_in;
                                const adjOut = deriveFromSites(rec.sites, 'clock_out', 'max') || rec.adjusted_clock_out;
                                const isSelected = selectedRows.has(rec.date);
                                return (
                                  <div
                                    key={rec.date}
                                    className={`bg-white border border-gray-200 rounded-xl p-4 ${isSelected ? 'ring-2 ring-red-200' : ''}`}
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex items-start gap-2 min-w-0">
                                        {!(rec.isPreEmployment || rec.isPostEmployment) && (
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => {}}
                                            onClick={(e) => onRowSelect(rec, rowIndex, e)}
                                            className="mt-0.5 rounded border-gray-300 text-red-600 accent-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                                            title="Select row"
                                          />
                                        )}
                                        <div className="min-w-0">
                                          <p className="text-sm font-medium text-gray-900">{formatDate(rec.date)}</p>
                                          <div className="flex items-center gap-1.5 flex-wrap">
                                            <p className="text-xs text-gray-500">{rec.day_of_week}</p>
                                            <ScheduleOverrideDateBadge rec={rec} statusIsDayOff={status.label === 'Day Off'} />
                                            <LastWorkingSessionBadge rec={rec} employee={selectedEmployee} />
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                        <span
                                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.cls}`}
                                          title={status.title}
                                        >
                                          {status.label}
                                        </span>
                                        {isLocked && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            Locked
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="mt-3 space-y-2">
                                      <div className="flex items-start justify-between gap-3 text-sm">
                                        <span className="text-gray-500 flex-shrink-0">Site</span>
                                        <span className="text-gray-900 text-right truncate min-w-0">{getSiteSummaryLabel(rec)}</span>
                                      </div>
                                      <div className="flex items-start justify-between gap-3 text-sm">
                                        <span className="text-gray-500 flex-shrink-0">Adjusted In/Out</span>
                                        <span className="text-gray-900 text-right tabular-nums font-mono text-xs">
                                          {adjIn || '-'} – {adjOut || '-'}
                                        </span>
                                      </div>
                                      <div className="flex items-start justify-between gap-3 text-sm">
                                        <span className="text-gray-500 flex-shrink-0">Regular / OT</span>
                                        <span className="text-gray-900 text-right tabular-nums">
                                          {getDisplayRegularTotal(rec).toFixed(2)}h
                                          {' / '}
                                          {(getDisplayWeekdayOt(rec) + getDisplayRestDayOt(rec)).toFixed(2)}h OT
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              {employeeDailyRecords.length === 0 && (
                                <div className="text-center text-gray-500 py-6 text-sm">
                                  No attendance records for this employee.
                                </div>
                              )}
                              {employeeDailyRecords.length > 0 && (
                                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                                  <p className="text-sm font-semibold text-gray-900 mb-2">Totals</p>
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                      <span className="text-gray-500">Regular</span>
                                      <p className="font-semibold tabular-nums">
                                        {employeeDailyRecords.reduce((sum, r) => sum + getDisplayRegularTotal(r), 0).toFixed(2)}h
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Workday OT</span>
                                      <p className="font-semibold tabular-nums">
                                        {employeeDailyRecords.reduce((sum, r) => sum + getDisplayWeekdayOt(r), 0).toFixed(2)}h
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Rest Day OT</span>
                                      <p className="font-semibold tabular-nums">
                                        {employeeDailyRecords.reduce((sum, r) => sum + getDisplayRestDayOt(r), 0).toFixed(2)}h
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Days worked</span>
                                      <p className="font-semibold tabular-nums">
                                        {employeeDailyRecords.filter(r => r.total_hours > 0).length}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="hidden md:block">
                            <table className="min-w-full table-fixed table-status-rail text-sm">
                              <colgroup>
                                <col style={{ width: `${COLUMN_WIDTHS_PX.checkbox}px` }} />
                                <col style={{ width: `${COLUMN_WIDTHS_PX.dateDay}px` }} />
                                <col style={{ width: `${COLUMN_WIDTHS_PX.status}px` }} />
                                <col style={{ width: `${COLUMN_WIDTHS_PX.sites}px` }} />
                                <col style={{ width: `${COLUMN_WIDTHS_PX.actualIn}px` }} />
                                <col style={{ width: `${COLUMN_WIDTHS_PX.actualOut}px` }} />
                                <col style={{ width: `${COLUMN_WIDTHS_PX.adjustedIn}px` }} />
                                <col style={{ width: `${COLUMN_WIDTHS_PX.adjustedOut}px` }} />
                                <col style={{ width: `${COLUMN_WIDTHS_PX.total}px` }} />
                                <col style={{ width: `${COLUMN_WIDTHS_PX.lunch}px` }} />
                                <col style={{ width: `${COLUMN_WIDTHS_PX.dinner}px` }} />
                                <col style={{ width: `${COLUMN_WIDTHS_PX.workdayOt}px` }} />
                                <col style={{ width: `${COLUMN_WIDTHS_PX.restDayOt}px` }} />
                                <col style={{ width: `${COLUMN_WIDTHS_PX.actions}px` }} />
                              </colgroup>
                              <thead className="table-header sticky top-0 z-10">
                                <tr>
                                  <th className="table-header-cell text-center border-l-4 border-transparent">
                                    <input
                                      type="checkbox"
                                      checked={employeeDailyRecords.length > 0 && 
                                               employeeDailyRecords.every(r => selectedRows.has(r.date))}
                                      onChange={onSelectAll}
                                      className="rounded border-gray-300 text-red-600 accent-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                                      title="Select all rows"
                                    />
                                  </th>
                                  <th className="table-header-cell">Date/Day</th>
                                  <th className="table-header-cell text-center">Status</th>
                                  <th className="table-header-cell">Sites</th>
                                  <th className="table-header-cell text-right">Actual In</th>
                                  <th className="table-header-cell text-right">Actual Out</th>
                                  <th className="table-header-cell text-right bg-stone-100/70">Adjusted In</th>
                                  <th className="table-header-cell text-right bg-stone-100/70">Adjusted Out</th>
                                  <th className="table-header-cell text-right bg-stone-100/70">Total</th>
                                  <th className="table-header-cell text-right bg-stone-100/70">Lunch</th>
                                  <th className="table-header-cell text-right bg-stone-100/70">Dinner</th>
                                  <th className="table-header-cell text-right whitespace-nowrap bg-orange-50">Workday OT</th>
                                  <th className="table-header-cell text-right whitespace-nowrap bg-orange-50">Rest Day OT</th>
                                  <th className="table-header-cell text-right">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {employeeDailyRecords.map((rec) => {
                                  const isExpanded = expandedRows.has(rec.date);
                                  // Note: isEditing removed - using cell-based editing instead
                                  const isLocked = rec.lock_status === 'locked';
                                  const hasSites = rec.sites && rec.sites.length > 0;
                                  const totalEntries = hasSites ? rec.sites.length : 0;
                                  const workSiteCount = hasSites ? rec.sites.filter(s => s.status === 'Present').length : 0;
                                  const siteCount = totalEntries || (rec.project_code ? 1 : 0);
                                  
                                  // Row chrome split: backgrounds stay on <tr>; left-edge
                                  // status bars MUST paint on the first <td>. Borders on
                                  // <tr> are unreliable in HTML tables (only the first
                                  // tbody row often shows), which hid half-day leave /
                                  // AM recovery / exempt cues on later dates.
                                  // Always reserve the 4px rail (transparent by default)
                                  // so checkboxes do not shift and collapse cannot steal
                                  // a coloured edge into the sticky header.
                                  let rowDenseClass = 'table-row-dense';
                                  let rowBgClass = 'bg-white';
                                  let rowEdgeClass = 'border-l-4 border-transparent';
                                  if (rec.isPreEmployment || rec.isPostEmployment) {
                                    rowBgClass = 'bg-gray-200/60 text-gray-400';
                                  } else if (isLocked) {
                                    rowDenseClass = '';
                                    rowBgClass = 'bg-green-50 hover:bg-green-100';
                                  } else if (rec.is_public_holiday) {
                                    rowDenseClass = '';
                                    rowBgClass = 'bg-purple-50 hover:bg-purple-100';
                                  } else if (rec.leave_application || (rec.leave_applications && rec.leave_applications.length > 0)) {
                                    const leaveApps = rec.leave_applications || (rec.leave_application ? [rec.leave_application] : []);
                                    const isHalfDayLeaveOnly = leaveApps.length > 0 && leaveApps.every(la => la.is_half_day);
                                    const isFullDaySplit = isHalfDayLeaveOnly && leaveApps.length >= 2
                                      && leaveApps.some(la => la.leave_period === 'AM') && leaveApps.some(la => la.leave_period === 'PM');
                                    if (isHalfDayLeaveOnly && !isFullDaySplit) {
                                      rowBgClass = 'bg-white';
                                      rowEdgeClass = 'border-l-4 border-l-amber-400';
                                    } else {
                                      rowBgClass = 'bg-amber-50 hover:bg-amber-100';
                                      rowDenseClass = '';
                                    }
                                  } else if (rec.recovery_type === 'am_only') {
                                    // AM-only recovery uses the half-day visual pattern
                                    // with the same yellow recovery hue as the chip.
                                    rowBgClass = 'bg-white';
                                    rowEdgeClass = 'border-l-4 border-l-yellow-400';
                                  } else if (rec.day_type === 'recovery' || isFullDayRecoveryRest(rec)) {
                                    rowBgClass = 'bg-yellow-50';
                                  } else if (rec.is_rest_day || rec.is_weekend) {
                                    rowBgClass = 'bg-gray-100';
                                  } else if (rec.needs_normalization) {
                                    rowDenseClass = '';
                                    rowBgClass = 'bg-amber-50/60 hover:bg-amber-100/60';
                                  } else if (rec.is_attendance_exempt) {
                                    rowBgClass = 'bg-white';
                                    rowEdgeClass = 'border-l-4 border-l-purple-300';
                                  } else if (rec.day_type === 'absent') {
                                    rowBgClass = 'bg-red-50';
                                  } else if (rec.day_type === 'upcoming') {
                                    rowBgClass = 'bg-gray-50';
                                  }
                                  
                                  // Note: No opacity for locked rows anymore - we use green background instead
                                  const rowOpacity = '';
                                  
                                  const rowIndex = employeeDailyRecords.findIndex(r => r.date === rec.date);
                                  const isSelected = selectedRows.has(rec.date);
                                  
                                  return (
                                    <React.Fragment key={rec.date}>
                                      {/* Parent Row - Summary (Accordion Header) */}
                                      <tr className={`${rowBgClass} ${rowDenseClass} ${rowOpacity} ${isSelected ? 'bg-blue-50' : ''}`}>
                                        {/* Checkbox column — left status edge lives here (not on <tr>) */}
                                        <td className={`px-4 py-3 text-center ${rowEdgeClass}`}>
                                          {(rec.isPreEmployment || rec.isPostEmployment) ? (
                                            <span className="text-gray-300">—</span>
                                          ) : (
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() => {}}
                                              onClick={(e) => onRowSelect(rec, rowIndex, e)}
                                              className="rounded border-gray-300 text-red-600 accent-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                                              title="Select row"
                                            />
                                          )}
                                        </td>
                                        {/* Date single-line compact with inline expand icon */}
                                        <td className="px-6 py-3">
                                          <div className="flex items-center gap-2 whitespace-nowrap">
                                            {/* Expand/Collapse Icon (inline) */}
                                            <div 
                                              onClick={() => siteCount > 1 && onToggleRow(rec.date)}
                                              className={siteCount > 1 ? 'cursor-pointer' : ''}
                                            >
                                              {siteCount > 1 ? (
                                                isExpanded ? (
                                                  <ChevronDown className="w-4 h-4 text-gray-600 transition-all duration-200 flex-shrink-0" />
                                                ) : (
                                                  <ChevronRight className="w-4 h-4 text-gray-600 transition-all duration-200 flex-shrink-0" />
                                                )
                                              ) : (
                                                <span className="w-4 h-4 inline-block flex-shrink-0"></span>
                                              )}
                                            </div>
                                            {/* Date Content - stacked: date above, full day name below */}
                                            <div className="min-w-0">
                                              <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                                                {formatDate(rec.date)}
                                                {/* Worker note: icon-only by default to keep rows uniform
                                                    during the row-by-row scan; click opens a lightweight
                                                    popover anchored to the icon (no modal, stays in place). */}
                                                {rec.has_custom_note && rec.ot_reason && (
                                                  <span className="relative inline-flex">
                                                    <button
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenNoteDate(openNoteDate === rec.date ? null : rec.date);
                                                      }}
                                                      className="p-1 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                                                      title="Worker left a note — click to view"
                                                      aria-label="View worker note"
                                                    >
                                                      <MessageSquareText className="w-4 h-4" />
                                                    </button>
                                                    {openNoteDate === rec.date && (
                                                      <>
                                                        <div
                                                          className="fixed inset-0 z-40"
                                                          onClick={(e) => { e.stopPropagation(); setOpenNoteDate(null); }}
                                                        />
                                                        <div
                                                          className="absolute left-0 top-7 z-50 w-64 rounded-xl border border-gray-200 bg-white p-3 shadow-lg"
                                                          onClick={(e) => e.stopPropagation()}
                                                        >
                                                          <div className="flex items-center gap-1.5 mb-1.5 text-amber-600">
                                                            <MessageSquareText className="w-3.5 h-3.5 flex-shrink-0" />
                                                            <span className="text-[11px] font-semibold uppercase tracking-wide">Worker note</span>
                                                          </div>
                                                          <p className="text-xs italic text-gray-700 leading-snug whitespace-normal break-words">
                                                            &ldquo;{rec.ot_reason}&rdquo;
                                                          </p>
                                                        </div>
                                                      </>
                                                    )}
                                                  </span>
                                                )}
                                              </div>
                                              <div className="text-xs text-gray-600 flex items-center gap-1.5 flex-wrap">
                                                <span>{rec.day_of_week}</span>
                                                <ScheduleOverrideDateBadge
                                                  rec={rec}
                                                  statusIsDayOff={isDayOffStatusChip(rec)}
                                                />
                                                <LastWorkingSessionBadge rec={rec} employee={selectedEmployee} />
                                              </div>
                                            </div>
                                          </div>
                                        </td>
                                        
                                        {/* Status — chip badges consistent with child rows */}
                                        <td className="px-6 py-3 text-center">
                                          {(() => {
                                            const WORK_LEAVE_TYPES = ['External Activity', 'Work From Home', 'On Course'];
                                            const hasClockData = rec.raw_clock_in || 
                                                                 rec.adjusted_clock_in || 
                                                                 deriveFromSites(rec.sites, 'raw_clock_in', 'min') ||
                                                                 deriveFromSites(rec.sites, 'clock_in', 'min') ||
                                                                 (rec.total_hours && rec.total_hours > 0);
                                            
                                            const leaves = rec.leave_applications && rec.leave_applications.length > 0
                                              ? rec.leave_applications
                                              : rec.leave_application ? [rec.leave_application] : [];
                                            const hasLeave = leaves.length > 0;
                                            const isHalfDay = hasLeave && leaves.some(la => la.is_half_day);

                                            // Deemed Present (Exempt) takes priority over the
                                            // hasClockData/Present branch: the backend populates
                                            // synthetic schedule-derived adjusted times for these
                                            // rows, so hasClockData is truthy — but advisors/
                                            // directors don't clock and must read "Exempt", with
                                            // their time attributed to the resolved bucket project.
                                            if (rec.is_attendance_exempt && !isHalfDay) {
                                              return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-purple-100 text-purple-700" title={rec.exempt_default_project_code ? `Exempt — Deemed Present. No clocking required; time attributed to ${rec.exempt_default_project_code}${rec.exempt_default_project_name ? ' (' + rec.exempt_default_project_name + ')' : ''}.` : 'Exempt — Deemed Present (no clocking required).'}>Exempt</span>;
                                            }

                                            const renderLeaveChip = (la, i) => {
                                              const fullName = la.leave_type || la.leave_type_abbr;
                                              const shortName = la.leave_type_abbr || la.leave_type;
                                              const label = la.is_half_day ? `${shortName} (${la.leave_period})` : shortName;
                                              const colorClasses = getLeaveTypeBadgeClass(fullName);
                                              // absence_proration entries are synthetic UL chips
                                              // emitted by the backend for days the employee was
                                              // absent without an approved LeaveApplication. Keep
                                              // the chip visually aligned with other leave types;
                                              // the tooltip carries the auto-classified context.
                                              const isAbsenceProration = la.source === 'absence_proration';
                                              const isProvisionalHl = ['active', 'reconciliation_required'].includes(
                                                la.hospitalisation_provisional_state
                                              );
                                              const tooltip = isAbsenceProration
                                                ? `${fullName} — auto-classified (no leave application; counted as Unpaid Leave deduction in payroll)`
                                                : isProvisionalHl
                                                  ? `${fullName} — certificate pending`
                                                  : fullName;
                                              return (
                                                <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic ring-1 ring-inset ${colorClasses}`} title={tooltip}>
                                                  {label}
                                                </span>
                                              );
                                            };

                                            if (hasClockData && isHalfDay) {
                                              const leavePeriods = new Set(leaves.filter(la => la.is_half_day && la.leave_period).map(la => la.leave_period));
                                              const exemptPeriods = new Set((rec.sites || [])
                                                .filter(s => s.is_attendance_exempt && s.half_day_slot)
                                                .flatMap(s => s.half_day_slot === 'FULL' ? ['AM', 'PM'] : [s.half_day_slot]));
                                              const allPeriodsCovered = leavePeriods.has('AM') && leavePeriods.has('PM');
                                              let presentPeriod = allPeriodsCovered ? null
                                                : leavePeriods.has('PM') && !leavePeriods.has('AM') ? 'AM'
                                                : leavePeriods.has('AM') && !leavePeriods.has('PM') ? 'PM'
                                                : null;

                                              if (allPeriodsCovered && !presentPeriod) {
                                                const physicalSites = (rec.sites || []).filter(s =>
                                                  !s.is_leave_session && !s.is_work_leave_session
                                                );
                                                const workedAM = physicalSites.some(s => s.half_day_slot === 'AM' || s.half_day_slot === 'FULL');
                                                const workedPM = physicalSites.some(s => s.half_day_slot === 'PM' || s.half_day_slot === 'FULL');
                                                if (workedPM && !workedAM) presentPeriod = 'PM';
                                                else if (workedAM && !workedPM) presentPeriod = 'AM';
                                              }

                                              // On a Saturday-half-day schedule (5.5-day staff)
                                              // PM is not a working period — the AM session IS
                                              // the entire scheduled day. Don't infer a Present
                                              // or Absent (PM); the AM leave chip alone is the
                                              // full status.
                                              if (rec.is_saturday_half_day && presentPeriod === 'PM') {
                                                presentPeriod = null;
                                              }
                                              if (presentPeriod && exemptPeriods.has(presentPeriod)) {
                                                presentPeriod = null;
                                              }

                                              // Verify the inferred present period has physical work
                                              // sessions — work leave sessions (EA/WFH) only cover
                                              // their own half and must not imply presence elsewhere.
                                              let absentPeriod = null;
                                              if (presentPeriod) {
                                                const physicalWorkSites = (rec.sites || []).filter(s =>
                                                  !s.is_leave_session && !s.is_work_leave_session
                                                  && (s.half_day_slot === presentPeriod || s.half_day_slot === 'FULL')
                                                );
                                                if (physicalWorkSites.length === 0) {
                                                  absentPeriod = presentPeriod;
                                                  presentPeriod = null;
                                                }
                                              }

                                              const chips = [];
                                              exemptPeriods.forEach((period) => {
                                                if (leavePeriods.has(period)) return;
                                                chips.push({ period, el: (
                                                  <span key={`exempt-${period}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-purple-100 text-purple-700 whitespace-nowrap" title={rec.exempt_default_project_code ? `Exempt — Deemed Present. No clocking required; time attributed to ${rec.exempt_default_project_code}${rec.exempt_default_project_name ? ' (' + rec.exempt_default_project_name + ')' : ''}.` : 'Exempt — Deemed Present (no clocking required).'}>
                                                    Exempt ({period})
                                                  </span>
                                                )});
                                              });
                                              if (presentPeriod) {
                                                chips.push({ period: presentPeriod, el: (
                                                  <span key="present" className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-green-100 text-green-700 whitespace-nowrap">
                                                    Present ({presentPeriod})
                                                  </span>
                                                )});
                                              } else if (absentPeriod) {
                                                chips.push({ period: absentPeriod, el: (
                                                  <span key="absent" className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-red-100 text-red-700 whitespace-nowrap">
                                                    Absent ({absentPeriod})
                                                  </span>
                                                )});
                                              }
                                              leaves.forEach((la, i) => {
                                                if (presentPeriod && la.is_half_day && la.leave_period === presentPeriod) return;
                                                chips.push({ period: la.leave_period || '', el: renderLeaveChip(la, i) });
                                              });
                                              chips.sort((a, b) => {
                                                const order = { 'AM': 0, 'PM': 1 };
                                                return (order[a.period] ?? 2) - (order[b.period] ?? 2);
                                              });
                                              return (
                                                <div className="flex flex-col gap-1 items-center">
                                                  {chips.map((c, idx) => <React.Fragment key={idx}>{c.el}</React.Fragment>)}
                                                </div>
                                              );
                                            } else if (rec.isPreEmployment) {
                                              return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-gray-200 text-gray-500">Not yet joined</span>;
                                            } else if (rec.isPostEmployment) {
                                              return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-gray-200 text-gray-500">Inactive</span>;
                                            } else if (rec.recovery_type === 'am_only') {
                                              // AM-only NW/RD recovery.  When the worker turned up
                                              // for the PM session, stack 'Recovery (AM)' over
                                              // 'Present (PM)' chips (mirrors the half-day-leave
                                              // chip pattern).  When they didn't, render a single
                                              // 'Recovery (AM)' chip — the AM rest credit is paid
                                              // either way.  Use the RAW-clock-event signal (not
                                              // ``hasClockData``) so synthetic AM Recovery sessions
                                              // with normalized_clock_in populated don't falsely
                                              // trigger the 'Present (PM)' chip on workers who
                                              // didn't actually clock in.
                                              const recoveryAmChip = (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-yellow-50 text-yellow-800 ring-1 ring-inset ring-yellow-600/30" title="AM Recovery (rest credit after nightwork / rest-day work)">Recovery (AM)</span>
                                              );
                                              const hasRealClockEvents = !!(
                                                rec.raw_clock_in
                                                || rec.actual_clock_in
                                                || deriveFromSites(rec.sites, 'raw_clock_in', 'min')
                                                || deriveFromSites(rec.sites, 'clock_in', 'min')
                                              );
                                              if (hasRealClockEvents) {
                                                return (
                                                  <div className="flex flex-col gap-1 items-center">
                                                    {recoveryAmChip}
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-green-100 text-green-700">Present (PM)</span>
                                                  </div>
                                                );
                                              }
                                              return recoveryAmChip;
                                            } else if (rec.day_type === 'recovery' || isFullDayRecoveryRest(rec)) {
                                              return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-yellow-50 text-yellow-800 ring-1 ring-inset ring-yellow-600/30">Recovery</span>;
                                            } else if (rec.is_public_holiday) {
                                              // PH is the primary status. Work-leaves (EA/WFH) on a PH
                                              // mean the employee is actively working — show their leave
                                              // chip instead. Non-work leave is never consumed on a PH
                                              // (backend strips it), so only PH chip is shown.
                                              const allWorkLeave = hasLeave && leaves.every(
                                                la => WORK_LEAVE_TYPES.includes(la.leave_type || la.leave_type_abbr)
                                              );
                                              if (allWorkLeave && hasLeave) {
                                                return (
                                                  <div className="flex flex-col gap-1 items-center">
                                                    {leaves.map((la, i) => renderLeaveChip(la, i))}
                                                  </div>
                                                );
                                              }
                                              const phChip = (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-purple-100 text-purple-700" title={rec.public_holiday_name || 'Public Holiday'}>PH</span>
                                              );
                                              // PH + worked: stack 'PH' (top) and 'Present' (below) so it's
                                              // visually clear the worker came in on the holiday.
                                              //
                                              // We must NOT use ``hasClockData`` here — that boolean
                                              // also matches the SYNTHETIC PH-allowance record that
                                              // the backend builds for PH days the employee skipped
                                              // (it sets ``adjusted_clock_in`` to the schedule start
                                              // and ``total_hours`` to the contracted shift hours so
                                              // the day still earns PH pay).  Stacking 'Present' on
                                              // top of those synthetic fields would falsely tell HR
                                              // the worker showed up.  Use the RAW-clock-event
                                              // signal instead: ``rec.raw_clock_in`` is only
                                              // populated when an actual ClockEvent exists (it
                                              // mirrors ``attendance_raw_clock_in`` on the AR), so
                                              // it correctly distinguishes "worked the PH" from
                                              // "had the PH off".  Multi-site days fall through to
                                              // the sites-level raw_clock_in.
                                              const hasRealClockEvents = !!(
                                                rec.raw_clock_in
                                                || rec.actual_clock_in
                                                || deriveFromSites(rec.sites, 'raw_clock_in', 'min')
                                              );
                                              if (hasRealClockEvents) {
                                                return (
                                                  <div className="flex flex-col gap-1 items-center">
                                                    {phChip}
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-green-100 text-green-700">Present</span>
                                                  </div>
                                                );
                                              }
                                              return phChip;
                                            } else if (hasLeave) {
                                              // Full-day leave must take priority over hasClockData —
                                              // paid leave days now have adjusted_clock_in populated by
                                              // the backend, but the status must still show the leave type.
                                              // Deduplicate: if AM and PM are the same leave type, show one chip.
                                              const leaveTypes = leaves.map(la => la.leave_type || la.leave_type_abbr);
                                              const allSameType = leaveTypes.length > 1 && leaveTypes.every(t => t === leaveTypes[0]);
                                              const deduped = allSameType ? [leaves[0]] : leaves;
                                              const renderDeduped = (la, i) => {
                                                const fullName = la.leave_type || la.leave_type_abbr;
                                                const shortName = la.leave_type_abbr || la.leave_type;
                                                const showPeriod = !allSameType && la.is_half_day;
                                                const label = showPeriod ? `${shortName} (${la.leave_period})` : shortName;
                                                const colorClasses = getLeaveTypeBadgeClass(fullName);
                                                const isAbsenceProration = la.source === 'absence_proration';
                                                const isProvisionalHl = ['active', 'reconciliation_required'].includes(
                                                  la.hospitalisation_provisional_state
                                                );
                                                const tooltip = isAbsenceProration
                                                  ? `${fullName} — auto-classified (no leave application; counted as Unpaid Leave deduction in payroll)`
                                                  : isProvisionalHl
                                                    ? `${fullName} — certificate pending`
                                                    : fullName;
                                                return (
                                                  <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic ring-1 ring-inset ${colorClasses}`} title={tooltip}>
                                                    {label}
                                                  </span>
                                                );
                                              };
                                              return (
                                                <div className="flex flex-col gap-1 items-center">
                                                  {deduped.map(renderDeduped)}
                                                </div>
                                              );
                                            } else if (hasClockData && rec.needs_normalization) {
                                              return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-amber-100 text-amber-700" title="Clock events exist but normalization has not processed this day yet. Re-run Adjust & Lock to normalize.">Present</span>;
                                            } else if (hasClockData) {
                                              return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-green-100 text-green-700">Present</span>;
                                            } else if (rec.schedule_override_type === 'day_off') {
                                              return (
                                                <span
                                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-gray-100 text-gray-500"
                                                  title={getScheduleOverrideInfo(rec)?.title}
                                                >
                                                  Day Off
                                                </span>
                                              );
                                            } else if (rec.is_rest_day || rec.is_weekend) {
                                              return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-gray-100 text-gray-500">Rest Day</span>;
                                            } else if (rec.day_type === 'absent') {
                                              return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-red-100 text-red-700">Absent</span>;
                                            } else if (rec.day_type === 'upcoming') {
                                              return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic bg-gray-50 text-gray-400">Upcoming</span>;
                                            } else {
                                              return <span className="text-gray-400">-</span>;
                                            }
                                          })()}
                                        </td>
                                        
                                        {/* Sites - Editable for single site, badge for multiple */}
                                        <td className="px-6 py-3">
                                          {siteCount > 1 ? (
                                            <div className="flex justify-center">
                                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                                                {siteCount}
                                              </span>
                                            </div>
                                          ) : siteCount === 1 ? (
                                            editingSiteCell === `${rec.date}-site` && isRowEditable(rec) ? (
                                              renderSiteCombobox({
                                                rec,
                                                currentLabel: rec.sites?.[0]?.site_name || rec.sites?.[0]?.project_code || 'Select site...',
                                                sessionIdOrIndex: rec.sites?.[0]?.id || null,
                                              })
                                            ) : (
                                              (() => {
                                                const s0 = rec.sites?.[0];
                                                const hasLocation = s0?.location && s0.location !== 'Unknown' && s0.location !== 'Offsite' && s0.location !== 'Leave';
                                                const displayName = hasLocation ? s0.location : (s0?.site_name || s0?.project_code || rec.project_name || rec.project_code || '-');
                                                const isBlankSite = !displayName || displayName === '-';
                                                const projCode = s0?.project_code || rec.project_code;
                                                const showSubtitle = !isBlankSite && projCode && displayName !== projCode;
                                                const isVirtualSession = s0?.is_work_leave_session || s0?.is_leave_session;
                                                const canEditPaidLeave = isRowEditable(rec) && rec.is_paid_leave;
                                                const canEditSite = canEditPaidLeave || (isRowEditable(rec) && !isVirtualSession && !!rec.attendance_record_id);
                                                const stateTitle = canEditSite
                                                  ? (isBlankSite ? 'Click to assign project/site for labour cost allocation' : 'Click to change site')
                                                  : (rec.lock_status === 'locked' && rec.is_paid_leave
                                                    ? 'No project allocated — unlock this day to assign'
                                                    : (isVirtualSession
                                                      ? 'Project from leave application'
                                                      : (!rec.attendance_record_id ? 'No attendance record (leave day)' : 'Row is locked')));
                                                const tooltip = isBlankSite
                                                  ? stateTitle
                                                  : `${displayName}${showSubtitle ? ` (${projCode})` : ''} — ${stateTitle}`;
                                                return (
                                                  <div
                                                    onClick={() => canEditSite && onSiteClick(rec.date)}
                                                    className={`flex flex-col min-w-0 ${canEditSite ? 'hover:bg-blue-50 cursor-pointer' : isVirtualSession ? '' : 'cursor-not-allowed'}`}
                                                    title={tooltip}
                                                  >
                                                    {isBlankSite ? (
                                                      <span className={`block truncate text-xs ${canEditSite ? 'text-gray-400 font-medium' : 'text-gray-400'}`}>
                                                        {canEditSite ? 'Assign site' : (rec.is_paid_leave ? 'Unallocated' : '-')}
                                                      </span>
                                                    ) : (
                                                      <span className="block truncate text-xs text-gray-700 font-medium">{displayName}</span>
                                                    )}
                                                    {showSubtitle && (
                                                      <span className="block truncate text-xs text-gray-500 mt-0.5">{projCode}</span>
                                                    )}
                                                  </div>
                                                );
                                              })()
                                            )
                                          ) : rec.is_paid_leave ? (
                                            editingSiteCell === `${rec.date}-site` ? (
                                              renderSiteCombobox({
                                                rec,
                                                currentLabel: 'Select site...',
                                                sessionIdOrIndex: null,
                                              })
                                            ) : (
                                              <div
                                                onClick={() => isRowEditable(rec) && onSiteClick(rec.date)}
                                                className={`px-2 py-1 rounded ${isRowEditable(rec) ? 'hover:bg-blue-50 cursor-pointer' : ''}`}
                                                title={isRowEditable(rec)
                                                  ? 'Click to assign project/site for labour cost allocation'
                                                  : (rec.lock_status === 'locked'
                                                    ? 'No project allocated — unlock this day to assign'
                                                    : '')}
                                              >
                                                <span className="text-gray-400">
                                                  {isRowEditable(rec) ? 'Assign site' : 'Unallocated'}
                                                </span>
                                              </div>
                                            )
                                          ) : (
                                            <span className="text-gray-400">-</span>
                                          )}
                                        </td>
                                        
                                        {/* Actual In — device tap, not editable from payroll */}
                                        <td className="px-6 py-3 text-right font-mono text-sm text-gray-900">
                                          <div
                                            className="px-2 py-1"
                                            title="Device clock-in. Correct a wrong tap on Attendance Daily or Realtime."
                                          >
                                            {rec.raw_clock_in || deriveFromSites(rec.sites, 'raw_clock_in', 'min') || <span className="text-gray-400">-</span>}
                                          </div>
                                        </td>
                                        
                                        {/* Actual Out — device tap, not editable from payroll */}
                                        <td className="px-6 py-3 text-right font-mono text-sm text-gray-900">
                                          <div
                                            className="px-2 py-1 flex flex-col items-end"
                                            title="Device clock-out. Correct a wrong tap on Attendance Daily or Realtime."
                                          >
                                            <span>{rec.raw_clock_out || deriveFromSites(rec.sites, 'raw_clock_out', 'max') || <span className="text-gray-400">-</span>}</span>
                                            {(rec.raw_clock_out_is_next_day || rec.sites?.some(s => s.raw_clock_out_is_next_day)) && (
                                              <InfoBadge color="amber" label="+1d" size="xs" />
                                            )}
                                          </div>
                                        </td>
                                        
                                        {/* Normalized Clock In */}
                                        <td className="px-6 py-3 text-right font-mono text-sm text-gray-900">
                                          {rec.sites && rec.sites.length > 1 ? (
                                            <div
                                              className="px-2 py-1 rounded cursor-not-allowed"
                                              title="Edit on individual sessions below"
                                            >
                                              {deriveFromSites(rec.sites, 'clock_in', 'min') || rec.adjusted_clock_in || <span className="text-gray-400">-</span>}
                                            </div>
                                          ) : editingCell === `${rec.date}-adjusted_clock_in` ? (
                                            <input
                                              type="text"
                                              value={cellValue}
                                              onChange={(e) => setCellValue(e.target.value)}
                                              onBlur={() => onCellSave(rec, 'adjusted_clock_in')}
                                              onKeyDown={(e) => e.key === 'Enter' && onCellSave(rec, 'adjusted_clock_in')}
                                              className="input-admin w-full px-2 py-1 border border-red-500 rounded"
                                              placeholder="HH:MM"
                                              autoFocus
                                              disabled={savingCell}
                                            />
                                          ) : (
                                            <div
                                              onClick={() => canEditAdjustedClock(rec) && onCellClick(rec.date, 'adjusted_clock_in', rec.adjusted_clock_in || deriveFromSites(rec.sites, 'adjusted_clock_in', 'min'))}
                                              className={`px-2 py-1 rounded ${canEditAdjustedClock(rec) ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-not-allowed'}`}
                                              title={adjustedClockEditTitle(rec)}
                                            >
                                              {rec.adjusted_clock_in || deriveFromSites(rec.sites, 'adjusted_clock_in', 'min') || <span className="text-gray-400">-</span>}
                                            </div>
                                          )}
                                        </td>
                                        
                                        {/* Normalized Clock Out */}
                                        <td className="px-6 py-3 text-right font-mono text-sm text-gray-900">
                                          {rec.sites && rec.sites.length > 1 ? (
                                            <div
                                              className="px-2 py-1 rounded cursor-not-allowed"
                                              title="Edit on individual sessions below"
                                            >
                                              {rec.adjusted_clock_out || deriveFromSites(rec.sites, 'adjusted_clock_out', 'max') || rec.sites.filter(s => s.status === 'Present').slice(-1)[0]?.clock_out || <span className="text-gray-400">-</span>}
                                            </div>
                                          ) : editingCell === `${rec.date}-adjusted_clock_out` ? (
                                            <input
                                              type="text"
                                              value={cellValue}
                                              onChange={(e) => setCellValue(e.target.value)}
                                              onBlur={() => onCellSave(rec, 'adjusted_clock_out')}
                                              onKeyDown={(e) => e.key === 'Enter' && onCellSave(rec, 'adjusted_clock_out')}
                                              className="input-admin w-full px-2 py-1 border border-red-500 rounded"
                                              placeholder="HH:MM"
                                              autoFocus
                                              disabled={savingCell}
                                            />
                                          ) : (
                                            <div
                                              onClick={() => canEditAdjustedClock(rec) && onCellClick(rec.date, 'adjusted_clock_out', rec.adjusted_clock_out || deriveFromSites(rec.sites, 'adjusted_clock_out', 'max'))}
                                              className={`px-2 py-1 rounded ${canEditAdjustedClock(rec) ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-not-allowed'}`}
                                              title={adjustedClockEditTitle(rec)}
                                            >
                                              {rec.adjusted_clock_out || deriveFromSites(rec.sites, 'adjusted_clock_out', 'max') || <span className="text-gray-400">-</span>}
                                            </div>
                                          )}
                                        </td>
                                        
                                        {/* Total Hours (regular only — OT is shown in dedicated OT columns) */}
                                        <td className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
                                          {getDisplayRegularTotal(rec) > 0 ? `${getDisplayRegularTotal(rec).toFixed(2)}h` : '-'}
                                        </td>
                                        
                                        {/* Lunch Break */}
                                        <td className="px-6 py-3 text-right text-sm text-gray-900">
                                          {editingCell === `${rec.date}-lunch_break_hours` ? (
                                            <input
                                              type="text"
                                              value={cellValue}
                                              onChange={(e) => setCellValue(e.target.value)}
                                              onBlur={() => onCellSave(rec, 'lunch_break_hours')}
                                              onKeyDown={(e) => e.key === 'Enter' && onCellSave(rec, 'lunch_break_hours')}
                                              className="input-admin w-full px-2 py-1 border border-red-500 rounded text-right"
                                              placeholder="0.0"
                                              autoFocus
                                              disabled={savingCell}
                                            />
                                          ) : (
                                            <div
                                              onClick={() => isRowEditable(rec) && onCellClick(rec.date, 'lunch_break_hours', rec.lunch_break_hours)}
                                              className={`px-2 py-1 rounded ${isRowEditable(rec) ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-not-allowed'}`}
                                            >
                                              {rec.lunch_break_hours ? `${rec.lunch_break_hours}h` : '-'}
                                            </div>
                                          )}
                                        </td>
                                        
                                        {/* Dinner Break */}
                                        <td className="px-6 py-3 text-right text-sm text-gray-900">
                                          {editingCell === `${rec.date}-dinner_break_hours` ? (
                                            <input
                                              type="text"
                                              value={cellValue}
                                              onChange={(e) => setCellValue(e.target.value)}
                                              onBlur={() => onCellSave(rec, 'dinner_break_hours')}
                                              onKeyDown={(e) => e.key === 'Enter' && onCellSave(rec, 'dinner_break_hours')}
                                              className="input-admin w-full px-2 py-1 border border-red-500 rounded text-right"
                                              placeholder="0.0"
                                              autoFocus
                                              disabled={savingCell}
                                            />
                                          ) : (
                                            <div
                                              onClick={() => isRowEditable(rec) && onCellClick(rec.date, 'dinner_break_hours', rec.dinner_break_hours)}
                                              className={`px-2 py-1 rounded ${isRowEditable(rec) ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-not-allowed'}`}
                                            >
                                              {rec.dinner_break_hours ? `${rec.dinner_break_hours}h` : '-'}
                                            </div>
                                          )}
                                        </td>
                                        
                                        {/* Workday OT Hours */}
                                        <td className="px-6 py-3 text-right text-sm text-gray-900">
                                          {editingCell === `${rec.date}-weekday_ot_hours` ? (
                                            <input
                                              type="text"
                                              value={cellValue}
                                              onChange={(e) => setCellValue(e.target.value)}
                                              onBlur={() => onCellSave(rec, 'weekday_ot_hours')}
                                              onKeyDown={(e) => e.key === 'Enter' && onCellSave(rec, 'weekday_ot_hours')}
                                              className="input-admin w-full px-2 py-1 border border-red-500 rounded text-right"
                                              placeholder="0.0"
                                              autoFocus
                                              disabled={savingCell}
                                            />
                                          ) : (
                                            <div
                                              onClick={() => isRowEditable(rec) && onCellClick(rec.date, 'weekday_ot_hours', getDisplayWeekdayOt(rec))}
                                              className={`px-2 py-1 rounded ${isRowEditable(rec) ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-not-allowed'}`}
                                            >
                                              <div className="flex flex-col items-end gap-0.5">
                                                <div className="flex items-center gap-1.5">
                                                  <span>{getDisplayWeekdayOt(rec) ? `${getDisplayWeekdayOt(rec).toFixed(1)}h` : '-'}</span>
                                                  {rec.ot_source === 'declared' && rec.computed_ot_hours > 0 && Math.abs(getDisplayWeekdayOt(rec) - rec.computed_ot_hours) > 0.01 && (
                                                    <span
                                                      className="text-orange-500 cursor-help"
                                                      title={`Declared: ${getDisplayWeekdayOt(rec).toFixed(1)}h | Computed: ${rec.computed_ot_hours.toFixed(1)}h (${(rec.computed_ot_hours - getDisplayWeekdayOt(rec)).toFixed(1)}h gap)`}
                                                    >
                                                      <AlertTriangle className="w-3.5 h-3.5" />
                                                    </span>
                                                  )}
                                                </div>
                                                {rec.ot_source === 'computed' && getDisplayWeekdayOt(rec) > 0 && (
                                                  <InfoBadge color="amber" label="computed" size="xs" />
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </td>
                                        
                                        {/* Rest Day OT Hours */}
                                        <td className="px-6 py-3 text-right text-sm text-gray-900">
                                          {editingCell === `${rec.date}-rest_day_ot_hours` ? (
                                            <input
                                              type="text"
                                              value={cellValue}
                                              onChange={(e) => setCellValue(e.target.value)}
                                              onBlur={() => onCellSave(rec, 'rest_day_ot_hours')}
                                              onKeyDown={(e) => e.key === 'Enter' && onCellSave(rec, 'rest_day_ot_hours')}
                                              className="input-admin w-full px-2 py-1 border border-red-500 rounded text-right"
                                              placeholder="0.0"
                                              autoFocus
                                              disabled={savingCell}
                                            />
                                          ) : (
                                            <div
                                              onClick={() => isRowEditable(rec) && onCellClick(rec.date, 'rest_day_ot_hours', getDisplayRestDayOt(rec))}
                                              className={`px-2 py-1 rounded ${isRowEditable(rec) ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-not-allowed'}`}
                                            >
                                              {getDisplayRestDayOt(rec) ? `${getDisplayRestDayOt(rec).toFixed(1)}h` : '-'}
                                            </div>
                                          )}
                                        </td>
                                        
                                        {/* Actions: Lock/Unlock button for parent row */}
                                        <td className="px-6 py-3 text-right">
                                          {(rec.isPreEmployment || rec.isPostEmployment) ? (
                                            <span className="text-xs text-gray-400 italic" title={rec.isPreEmployment ? 'Not yet joined — no attendance applicable' : 'Inactive — no attendance applicable'}>
                                              N/A
                                            </span>
                                          ) : (() => {
                                            const canLockNow = isDayLockableNow(rec, todayIso);
                                            const canToggleLock = canLockNow || isLocked;
                                            const isRenormalizing = rowRenormalizing?.has(rec.date);
                                            const canRenormalize = isDayRenormalizableNow(rec, todayIso);
                                            return (
                                              <div className="inline-flex items-center justify-end gap-1.5">
                                                {/* Re-normalize is a contextual corrective action: only
                                                    surfaced where it can actually run (unlocked + concluded).
                                                    Hiding it on locked/future rows keeps the Actions column
                                                    a clean single lock icon in its resting state. */}
                                                {onRowRenormalize && canRenormalize && (
                                                  <button
                                                    onClick={() => !isRenormalizing && onRowRenormalize(rec)}
                                                    disabled={isRenormalizing}
                                                    className="icon-button-admin"
                                                    title="Re-normalize this day (recompute from clock data)"
                                                    aria-label="Re-normalize this day"
                                                  >
                                                    <RefreshCw className={`w-3.5 h-3.5${isRenormalizing ? ' animate-spin' : ''}`} />
                                                  </button>
                                                )}
                                                <button
                                                  onClick={() => canToggleLock && onRowLockToggle(rec)}
                                                  disabled={!canToggleLock}
                                                  className={`inline-flex items-center justify-center w-8 h-8 rounded-full border ${
                                                    !canToggleLock
                                                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                      : isLocked 
                                                        ? 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200' 
                                                        : 'bg-yellow-100 text-yellow-700 border-yellow-300 hover:bg-yellow-200'
                                                  }`}
                                                  title={
                                                    !canToggleLock
                                                      ? getLockBlockReason(rec, todayIso)
                                                      : isLocked
                                                        ? (formatLockAttribution(rec)
                                                            ? `${formatLockAttribution(rec)}\nClick to unlock`
                                                            : 'Unlock')
                                                          : rec.lock_ready_reason || 'Lock'
                                                  }
                                                >
                                                  {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                                                </button>
                                              </div>
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                      
                                      {/* Child Rows - Site Details (if expanded and has multiple sites) */}
                                      {isExpanded && hasSites && siteCount > 1 && (() => {
                                        const regularByIdx = rec.sites.map(s => {
                                          if (s.regular_hours != null) return parseFloat(s.regular_hours) || 0;
                                          if (s.is_leave_session) return parseFloat(s.hours) || 0;
                                          return 0;
                                        });

                                        const dayHasLeave = rec.sites.some(s => s.is_leave_session || s.is_work_leave_session);
                                        const dayHasNightwork = rec.sites.some(s => s.half_day_slot === 'NW');
                                        const showPeriodLabels = dayHasLeave || dayHasNightwork;
                                        return rec.sites.map((site, idx) => {
                                        const rawSlot = site.half_day_slot;
                                        const displaySlot = showPeriodLabels && (rawSlot === 'AM' || rawSlot === 'PM' || rawSlot === 'NW') ? rawSlot : null;
                                        const isPeriodLabel = displaySlot === 'AM' || displaySlot === 'PM' || displaySlot === 'NW';
                                        const isSlotBoundary = isPeriodLabel && (idx === 0 || rec.sites[idx - 1]?.half_day_slot !== rawSlot);

                                        return (
                                        <tr key={`${rec.date}-site-${idx}`} className={`${isSlotBoundary && idx > 0 ? 'border-t-2 border-indigo-200' : ''} bg-gray-50/70 table-row-dense`}>
                                          {/* Checkbox column (empty for child rows) — nested indent edge on first cell */}
                                          <td className="px-4 py-3 border-l-[3px] border-l-gray-300"></td>
                                          <td className="px-6 py-3 pl-14">
                                            {isSlotBoundary ? (
                                              displaySlot === 'NW' ? (
                                                <span className="inline-flex items-center px-1.5 py-px rounded-full text-[11px] font-semibold bg-indigo-100 text-indigo-700">
                                                  Night
                                                </span>
                                              ) : (
                                                <SessionPeriodChip session={displaySlot} />
                                              )
                                            ) : !rawSlot ? (
                                              <span className="text-xs font-medium text-gray-600">Session {idx + 1}</span>
                                            ) : null}
                                          </td>
                                          {/* Status badge for each child row */}
                                          <td className="px-6 py-3 text-center text-xs">
                                            {site.status ? (
                                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium not-italic ${
                                                site.status === 'Present'
                                                  ? 'bg-green-100 text-green-700'
                                                  : site.status === 'Exempt' || site.is_attendance_exempt
                                                    ? 'bg-purple-100 text-purple-700'
                                                  : site.status === 'Absent'
                                                    ? 'bg-red-100 text-red-700'
                                                    : site.is_recovery_session
                                                      // Recovery (AM) / Recovery Day synthetic
                                                      // sessions use the same warm yellow as the
                                                      // parent-row Recovery chip so the status
                                                      // column stays distinct from leave colors.
                                                      ? 'bg-yellow-50 text-yellow-800 ring-1 ring-inset ring-yellow-600/30'
                                                      : (site.is_work_leave_session || site.is_leave_session)
                                                        ? `ring-1 ring-inset ${getLeaveTypeBadgeClass(site.status_abbr || site.status)}`
                                                        : 'bg-gray-100 text-gray-600'
                                              }`} title={site.status}>
                                                {site.status_abbr || site.status}
                                              </span>
                                            ) : (
                                              <span className="text-gray-400">-</span>
                                            )}
                                          </td>
                                          {/* Sites column - Editable dropdown for each session */}
                                          <td className="px-6 py-3">
                                            {editingSiteCell === `${rec.date}-site-${site.id || idx}` && isRowEditable(rec) ? (
                                              renderSiteCombobox({
                                                rec,
                                                currentLabel: site.site_name || site.project_code || 'Select site...',
                                                sessionIdOrIndex: site.id || null,
                                              })
                                            ) : (
                                              (() => {
                                                const hasLoc = site.location && site.location !== 'Unknown' && site.location !== 'Offsite' && site.location !== 'Leave';
                                                const displayName = hasLoc ? site.location : (site.site_name || site.project_code || '-');
                                                const isBlankSite = !displayName || displayName === '-';
                                                const showSubtitle = !isBlankSite && site.project_code && displayName !== site.project_code;
                                                const isVirtualSession = site.is_work_leave_session || site.is_leave_session;
                                                const canEditSite = isRowEditable(rec) && !isVirtualSession && !!rec.attendance_record_id;
                                                const stateTitle = isBlankSite && rec.is_paid_leave
                                                  ? (rec.lock_status === 'locked'
                                                    ? 'No project allocated — unlock this day to assign'
                                                    : 'No project allocated')
                                                  : isVirtualSession
                                                    ? 'Project from leave application'
                                                  : (canEditSite
                                                    ? 'Click to change site'
                                                    : (!rec.attendance_record_id ? 'No attendance record (leave day)' : 'Row is locked'));
                                                const tooltip = isBlankSite
                                                  ? stateTitle
                                                  : `${displayName}${showSubtitle ? ` (${site.project_code})` : ''} — ${stateTitle}`;
                                                return (
                                                  <div
                                                    onClick={() => canEditSite && onSiteClick(rec.date, site.id || idx)}
                                                    className={`flex flex-col min-w-0 ${canEditSite ? 'hover:bg-blue-50 cursor-pointer' : ''}`}
                                                    title={tooltip}
                                                  >
                                                    <span className={`block truncate text-xs font-medium ${isBlankSite ? 'text-gray-400' : 'text-gray-700'}`}>
                                                      {isBlankSite && rec.is_paid_leave ? 'Unallocated' : displayName}
                                                    </span>
                                                    {showSubtitle && (
                                                      <span className="block truncate text-xs text-gray-500 mt-0.5">{site.project_code}</span>
                                                    )}
                                                  </div>
                                                );
                                              })()
                                            )}
                                          </td>
                                          {/* Actual In */}
                                          <td className="px-6 py-3 text-right font-mono text-xs text-gray-600"><div className="px-2 py-1">{site.raw_clock_in || '-'}</div></td>
                                          {/* Actual Out */}
                                          <td className="px-6 py-3 text-right font-mono text-xs text-gray-600">
                                            <div className="px-2 py-1 flex flex-col items-end">
                                              <span>{site.raw_clock_out || '-'}</span>
                                              {site.raw_clock_out_is_next_day && (
                                                <InfoBadge color="amber" label="+1d" size="xs" />
                                              )}
                                            </div>
                                          </td>
                                          {/* Adjusted In */}
                                          <td className="px-6 py-3 text-right font-mono text-xs text-gray-700">
                                            {editingCell === `${rec.date}-session-${site.id}-clock_in` ? (
                                              <input
                                                type="text"
                                                value={cellValue}
                                                onChange={(e) => setCellValue(e.target.value)}
                                                onBlur={() => onSessionCellSave(rec, site, 'clock_in')}
                                                onKeyDown={(e) => e.key === 'Enter' && onSessionCellSave(rec, site, 'clock_in')}
                                                className="input-admin w-full px-2 py-1 border border-red-500 rounded text-xs"
                                                placeholder="HH:MM"
                                                autoFocus
                                                disabled={savingCell}
                                              />
                                            ) : (
                                              <div
                                                onClick={() => isRowEditable(rec) && site.id && (() => { setEditingCell(`${rec.date}-session-${site.id}-clock_in`); setCellValue(site.clock_in || ''); })()}
                                                className={`px-2 py-1 rounded ${isRowEditable(rec) && site.id ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-not-allowed'}`}
                                                title={isRowEditable(rec) && site.id ? 'Click to edit' : ''}
                                              >
                                                {site.clock_in || '-'}
                                              </div>
                                            )}
                                          </td>
                                          {/* Adjusted Out */}
                                          <td className="px-6 py-3 text-right font-mono text-xs text-gray-700">
                                            {editingCell === `${rec.date}-session-${site.id}-clock_out` ? (
                                              <input
                                                type="text"
                                                value={cellValue}
                                                onChange={(e) => setCellValue(e.target.value)}
                                                onBlur={() => onSessionCellSave(rec, site, 'clock_out')}
                                                onKeyDown={(e) => e.key === 'Enter' && onSessionCellSave(rec, site, 'clock_out')}
                                                className="input-admin w-full px-2 py-1 border border-red-500 rounded text-xs"
                                                placeholder="HH:MM"
                                                autoFocus
                                                disabled={savingCell}
                                              />
                                            ) : (
                                              <div
                                                onClick={() => isRowEditable(rec) && site.id && (() => { setEditingCell(`${rec.date}-session-${site.id}-clock_out`); setCellValue(site.clock_out || ''); })()}
                                                className={`px-2 py-1 rounded ${isRowEditable(rec) && site.id ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-not-allowed'}`}
                                                title={isRowEditable(rec) && site.id ? 'Click to edit' : ''}
                                              >
                                                {site.clock_out || '-'}
                                              </div>
                                            )}
                                          </td>
                                          {/* Total (regular only — sequential allocation from contracted hours) */}
                                          <td className="px-6 py-3 text-right text-sm font-medium text-gray-700">
                                            {site.hours ? `${regularByIdx[idx].toFixed(2)}h` : '-'}
                                          </td>
                                          {/* Lunch */}
                                          <td className="px-6 py-3 text-right text-xs text-gray-600">
                                            {editingCell === `${rec.date}-child-${site.id || idx}-lunch_break_hours` ? (
                                              <input
                                                type="text"
                                                value={cellValue}
                                                onChange={(e) => setCellValue(e.target.value)}
                                                onBlur={() => onCellSave(rec, 'lunch_break_hours')}
                                                onKeyDown={(e) => e.key === 'Enter' && onCellSave(rec, 'lunch_break_hours')}
                                                className="input-admin w-full px-2 py-1 border border-red-500 rounded text-right"
                                                placeholder="0.0"
                                                autoFocus
                                                disabled={savingCell}
                                              />
                                            ) : (
                                              <div
                                                onClick={() => {
                                                  if (isRowEditable(rec) && site.lunch_break_hours) {
                                                    setEditingCell(`${rec.date}-child-${site.id || idx}-lunch_break_hours`);
                                                    setCellValue(rec.lunch_break_hours != null ? String(rec.lunch_break_hours) : '');
                                                  }
                                                }}
                                                className={`px-2 py-1 rounded ${isRowEditable(rec) && site.lunch_break_hours ? 'hover:bg-blue-50 cursor-pointer' : ''}`}
                                                title={isRowEditable(rec) && site.lunch_break_hours ? 'Click to edit' : ''}
                                              >
                                                {site.lunch_break_hours ? `${site.lunch_break_hours}h` : '-'}
                                              </div>
                                            )}
                                          </td>
                                          {/* Dinner */}
                                          <td className="px-6 py-3 text-right text-xs text-gray-600">
                                            {editingCell === `${rec.date}-child-${site.id || idx}-dinner_break_hours` ? (
                                              <input
                                                type="text"
                                                value={cellValue}
                                                onChange={(e) => setCellValue(e.target.value)}
                                                onBlur={() => onCellSave(rec, 'dinner_break_hours')}
                                                onKeyDown={(e) => e.key === 'Enter' && onCellSave(rec, 'dinner_break_hours')}
                                                className="input-admin w-full px-2 py-1 border border-red-500 rounded text-right"
                                                placeholder="0.0"
                                                autoFocus
                                                disabled={savingCell}
                                              />
                                            ) : (
                                              <div
                                                onClick={() => {
                                                  if (isRowEditable(rec) && site.dinner_break_hours) {
                                                    setEditingCell(`${rec.date}-child-${site.id || idx}-dinner_break_hours`);
                                                    setCellValue(rec.dinner_break_hours != null ? String(rec.dinner_break_hours) : '');
                                                  }
                                                }}
                                                className={`px-2 py-1 rounded ${isRowEditable(rec) && site.dinner_break_hours ? 'hover:bg-blue-50 cursor-pointer' : ''}`}
                                                title={isRowEditable(rec) && site.dinner_break_hours ? 'Click to edit' : ''}
                                              >
                                                {site.dinner_break_hours ? `${site.dinner_break_hours}h` : '-'}
                                              </div>
                                            )}
                                          </td>
                                          {/* Workday OT */}
                                          <td className="px-6 py-3 text-right text-xs text-gray-600">
                                            {editingCell === `${rec.date}-child-${site.id || idx}-weekday_ot_hours` ? (
                                              <input
                                                type="text"
                                                value={cellValue}
                                                onChange={(e) => setCellValue(e.target.value)}
                                                onBlur={() => onCellSave(rec, 'weekday_ot_hours')}
                                                onKeyDown={(e) => e.key === 'Enter' && onCellSave(rec, 'weekday_ot_hours')}
                                                className="input-admin w-full px-2 py-1 border border-red-500 rounded text-right"
                                                placeholder="0.0"
                                                autoFocus
                                                disabled={savingCell}
                                              />
                                            ) : (
                                              <div
                                                onClick={() => {
                                                  if (isRowEditable(rec)) {
                                                    setEditingCell(`${rec.date}-child-${site.id || idx}-weekday_ot_hours`);
                                                    setCellValue(site.weekday_ot_hours != null ? String(site.weekday_ot_hours) : '');
                                                  }
                                                }}
                                                className={`px-2 py-1 rounded ${isRowEditable(rec) ? 'hover:bg-blue-50 cursor-pointer' : ''}`}
                                                title={isRowEditable(rec) ? 'Click to edit' : ''}
                                              >
                                                {site.weekday_ot_hours ? `${parseFloat(site.weekday_ot_hours).toFixed(1)}h` : '-'}
                                              </div>
                                            )}
                                          </td>
                                          {/* Rest Day OT */}
                                          <td className="px-6 py-3 text-right text-xs text-gray-600">
                                            {editingCell === `${rec.date}-child-${site.id || idx}-rest_day_ot_hours` ? (
                                              <input
                                                type="text"
                                                value={cellValue}
                                                onChange={(e) => setCellValue(e.target.value)}
                                                onBlur={() => onCellSave(rec, 'rest_day_ot_hours')}
                                                onKeyDown={(e) => e.key === 'Enter' && onCellSave(rec, 'rest_day_ot_hours')}
                                                className="input-admin w-full px-2 py-1 border border-red-500 rounded text-right"
                                                placeholder="0.0"
                                                autoFocus
                                                disabled={savingCell}
                                              />
                                            ) : (
                                              <div
                                                onClick={() => {
                                                  if (isRowEditable(rec)) {
                                                    setEditingCell(`${rec.date}-child-${site.id || idx}-rest_day_ot_hours`);
                                                    setCellValue(site.rest_day_ot_hours != null ? String(site.rest_day_ot_hours) : '');
                                                  }
                                                }}
                                                className={`px-2 py-1 rounded ${isRowEditable(rec) ? 'hover:bg-blue-50 cursor-pointer' : ''}`}
                                                title={isRowEditable(rec) ? 'Click to edit' : ''}
                                              >
                                                {site.rest_day_ot_hours ? `${parseFloat(site.rest_day_ot_hours).toFixed(1)}h` : '-'}
                                              </div>
                                            )}
                                          </td>
                                          {/* Actions: Delete this session */}
                                          <td className="px-6 py-3 text-right text-xs">
                                            <button
                                              onClick={() => onDeleteSession(rec, site)}
                                              className="icon-button-danger"
                                              title="Delete session"
                                              aria-label="Delete session"
                                            >
                                              <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                      });
                                      })()}
                                    </React.Fragment>
                                  );
                                })}
                                {employeeDailyRecords.length === 0 && (
                                  <tr>
                                    <td colSpan={14} className="px-3 py-6 text-center text-gray-500">
                                      No attendance records for this employee.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                                <tr>
                                  {/* Checkbox column - empty */}
                                  <td className="px-4 py-4"></td>
                                  {/* Date/Day column */}
                                  <td className="px-6 py-4 text-left">
                                    <div className="flex items-center gap-3">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
                                        {employeeDailyRecords.filter(r => r.total_hours > 0).length} days
                                      </span>
                                      <span className="text-sm font-semibold text-gray-900">TOTALS</span>
                                    </div>
                                  </td>
                                  {/* Status column - empty */}
                                  <td className="px-6 py-4"></td>
                                  {/* Sites, Actual In, Actual Out, Adjusted In, Adjusted Out - empty */}
                                  <td className="px-6 py-4"></td>
                                  <td className="px-6 py-4"></td>
                                  <td className="px-6 py-4"></td>
                                  <td className="px-6 py-4"></td>
                                  <td className="px-6 py-4"></td>
                                  {/* Total Hours (regular only) */}
                                  <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                                    {employeeDailyRecords.reduce((sum, r) => sum + getDisplayRegularTotal(r), 0).toFixed(2)}h
                                  </td>
                                  {/* Lunch Break Total */}
                                  <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                                    {employeeDailyRecords.reduce((sum, r) => sum + (r.lunch_break_hours || 0), 0).toFixed(2)}h
                                  </td>
                                  {/* Dinner Break Total */}
                                  <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                                    {employeeDailyRecords.reduce((sum, r) => sum + (r.dinner_break_hours || 0), 0).toFixed(2)}h
                                  </td>
                                  {/* Workday OT */}
                                  <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                                    {employeeDailyRecords.reduce((sum, r) => sum + getDisplayWeekdayOt(r), 0).toFixed(2)}h
                                  </td>
                                  {/* Rest Day OT */}
                                  <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                                    {employeeDailyRecords.reduce((sum, r) => sum + getDisplayRestDayOt(r), 0).toFixed(2)}h
                                  </td>
                                  {/* Actions - empty */}
                                  <td className="px-6 py-4"></td>
                                </tr>
                              </tfoot>
                            </table>
                            </div>

                            {/* Bulk Action Bar - Sticky bottom bar when rows are selected */}
                            {selectedRows.size > 0 && (() => {
                              const selectedRecords = Array.from(selectedRows)
                                .map(date => recs.find(r => r.date === date))
                                .filter(Boolean);
                              const hasLockable = selectedRecords.some(
                                r => r.lock_status !== 'locked' && isDayLockableNow(r, todayIso)
                              );
                              const hasUnlockable = selectedRecords.some(
                                r => r.lock_status === 'locked' && !r.isPreEmployment && !r.isPostEmployment
                              );
                              return (
                                <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-6 py-3 z-20">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">
                                      {selectedRows.size} row(s) selected
                                    </span>
                                    <div className="flex items-center gap-3">
                                      {hasLockable && (
                                        <button
                                          onClick={onBulkLock}
                                          disabled={rowLocking.size > 0}
                                          className="btn btn-success"
                                        >
                                          <Lock className="w-4 h-4 mr-2" />
                                          {rowLocking.size > 0 ? 'Locking...' : 'Lock Selected'}
                                        </button>
                                      )}
                                      {hasUnlockable && (
                                        <button
                                          onClick={onBulkUnlock}
                                          disabled={rowLocking.size > 0}
                                          className="btn btn-warning"
                                        >
                                          <Unlock className="w-4 h-4 mr-2" />
                                          {rowLocking.size > 0 ? 'Unlocking...' : 'Unlock Selected'}
                                        </button>
                                      )}
                                      <button
                                        onClick={() => {
                                          setSelectedRows(new Set());
                                          setLastSelectedIndex(null);
                                        }}
                                        className="btn btn-admin-outline"
                                      >
                                        <X className="w-4 h-4 mr-2" />
                                        Clear
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </>
                      ) : (
                        <div className="flex-1 flex items-center justify-center text-gray-500">
                          <div className="text-center">
                            <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                            <p className="text-sm">Select an employee to view details</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-12 text-center">
                    <Settings className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-sm text-gray-600">Run adjustment to load employee attendance data</p>
                  </div>
                )}
              </div>
            );
          }
